import React from 'react';
import { Badge, Skeleton } from 'antd';
import { PlatformBreakdownProps } from './types';
import { PLATFORM_ICONS } from './constants';
import { BreakdownGrid, BreakdownCard, BreakdownHeader, BreakdownTitle, BreakdownTitleText, BreakdownStats, BreakdownStatRow, BreakdownStatLabel, BreakdownStatValue, IconWithColor } from './Styles';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const PlatformBreakdown: React.FC<PlatformBreakdownProps> = ({ platformBreakdown, loading = false }) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: isDarkMode ? 'dark' : 'light' } as const;

  if (loading || !platformBreakdown) {
    return (
      <ThemeProvider theme={theme}>
        <BreakdownGrid>
          {Array.from({ length: 2 }).map((_, index) => (
            <BreakdownCard key={index} variant={index === 0 ? 'blue' : 'green'}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </BreakdownCard>
          ))}
        </BreakdownGrid>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <BreakdownGrid>
        <BreakdownCard variant="blue">
          <BreakdownHeader>
            <BreakdownTitle>
              <IconWithColor icon={PLATFORM_ICONS.kiosk} iconcolor="#1890ff" />
              <BreakdownTitleText>Kiosk Orders</BreakdownTitleText>
            </BreakdownTitle>
            <Badge count={platformBreakdown.kiosk.orderCount || 0} showZero color="#1890ff" />
          </BreakdownHeader>
          <BreakdownStats>
            <BreakdownStatRow>
              <BreakdownStatLabel>Revenue:</BreakdownStatLabel>
              <BreakdownStatValue>{formatCurrency(platformBreakdown.kiosk.revenue)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Fees:</BreakdownStatLabel>
              <BreakdownStatValue color="#ff4d4f">{formatCurrency(platformBreakdown.kiosk.fees)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Net:</BreakdownStatLabel>
              <BreakdownStatValue color="#52c41a">{formatCurrency(platformBreakdown.kiosk.netEarnings)}</BreakdownStatValue>
            </BreakdownStatRow>
          </BreakdownStats>
        </BreakdownCard>

        <BreakdownCard variant="green">
          <BreakdownHeader>
            <BreakdownTitle>
              <IconWithColor icon={PLATFORM_ICONS.mobile} iconcolor="#52c41a" />
              <BreakdownTitleText>Mobile Orders</BreakdownTitleText>
            </BreakdownTitle>
            <Badge count={platformBreakdown.mobile.orderCount || 0} showZero color="#52c41a" />
          </BreakdownHeader>
          <BreakdownStats>
            <BreakdownStatRow>
              <BreakdownStatLabel>Revenue:</BreakdownStatLabel>
              <BreakdownStatValue>{formatCurrency(platformBreakdown.mobile.revenue)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Fees:</BreakdownStatLabel>
              <BreakdownStatValue color="#ff4d4f">{formatCurrency(platformBreakdown.mobile.fees)}</BreakdownStatValue>
            </BreakdownStatRow>
            <BreakdownStatRow>
              <BreakdownStatLabel>Net:</BreakdownStatLabel>
              <BreakdownStatValue color="#52c41a">{formatCurrency(platformBreakdown.mobile.netEarnings)}</BreakdownStatValue>
            </BreakdownStatRow>
          </BreakdownStats>
        </BreakdownCard>
      </BreakdownGrid>
    </ThemeProvider>
  );
};

export default PlatformBreakdown;
